import { useMemo, useState } from 'react';
import { SearchDateSingleForm } from 'src/components/common/form/search/SearchDateSingleForm';
import { SearchDropDownForm } from 'src/components/common/form/search/SearchDropDownForm';
import { SearchSectionForm } from 'src/components/common/form/search/SearchSectionForm';
import { defaultSysCode_Modal, SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useHRM2010_R01Store } from './Store_HRM2010_R01';

/** 검색 영역과 일괄 정상출근 버튼의 입력값이다. */
interface Props {
    onBulkClick: () => void; // 일괄 정상출근 확인창을 여는 함수
}

/** 근무일자·사업장·부서·근태·외출·근무조 검색 조건을 표시한다. */
export const HRM2010_R01SearchSection = ({ onBulkClick }: Props) => {
    /** 화면 검색 조건과 화면 전용 기준 목록을 읽는다. */
    const { HRM2010_R01Filter, setHRM2010_R01Filter, workShiftList, attendCodeList } = useHRM2010_R01Store();
    /** 사업장·부서 공통 목록을 읽는다. */
    const { compCdList, orgCdList } = useCommonCodeStore();
    /** 공통 날짜 선택기의 펼침 상태와 위치이다. */
    const [visible, setVisible] = useState(false);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    /** 사용 중인 근태코드 목록에서 위 그리드의 근태구분만 기억한다. */
    const attendOptions = useMemo(() => attendCodeList
        .filter(code => ['G1', 'G2', 'G3', 'G4'].includes(code.attendGroup)),
    [attendCodeList]);
    /** 외출 기록 유무만 선택하는 고정 코드 옵션이다. */
    const outOptions = useMemo<SysCodeDto[]>(() => [{
        ...defaultSysCode_Modal, minorCd: 'Y', codeNm: '외출 있음',
    }], []);
    /** DatePicker의 현지 날짜를 서버용 근무일자 문자열로 저장한다. */
    const setWorkDate = (date: Date | undefined) => {
        if (!date) return;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setHRM2010_R01Filter({ workDt: `${year}-${month}-${day}` });
    };

    return (
        <SearchSectionForm>
            {/* 근무일자: 출근일을 기준으로 조회한다. */}
            <SearchDateSingleForm label="근무일자" visible={visible} setVisible={setVisible}
                value={HRM2010_R01Filter.workDt ? new Date(`${HRM2010_R01Filter.workDt}T00:00:00`) : undefined}
                onChange={setWorkDate} pickerPosition={pickerPosition} setPickerPosition={setPickerPosition} />
            {/* 사업장 공통 목록이다. */}
            <SearchDropDownForm label="사업장" value={HRM2010_R01Filter.compCd} optionList={compCdList}
                onChangeItem={item => setHRM2010_R01Filter({ compCd: item?.compCd || '' })} />
            {/* 부서 공통 목록이다. */}
            <SearchDropDownForm label="부서" value={HRM2010_R01Filter.orgCd} optionList={orgCdList}
                onChangeItem={item => setHRM2010_R01Filter({ orgCd: item?.orgCd || '' })} />
            {/* 근태 G1~G4만 검색 조건으로 고른다. */}
            <SearchDropDownForm label="근태" value={HRM2010_R01Filter.attendCd} optionList={attendOptions}
                onChangeItem={item => setHRM2010_R01Filter({ attendCd: item?.attendCd || '' })} />
            {/* 외출 기록 유무를 고른다. */}
            <SearchDropDownForm label="외출" value={HRM2010_R01Filter.outYn} optionList={outOptions}
                onChangeItem={item => setHRM2010_R01Filter({ outYn: item?.minorCd || '' })} />
            {/* 사용 중인 근무조를 검색 조건으로 고른다. */}
            <SearchDropDownForm label="근무조" value={HRM2010_R01Filter.shiftCd} optionList={workShiftList}
                onChangeItem={item => setHRM2010_R01Filter({ shiftCd: item?.shiftCd || '' })} />
            {/* 일괄 정상출근 확인창을 여는 화면 전용 버튼이다. */}
            <button type="button" onClick={onBulkClick}
                className="flex-shrink-0 bg-white hover:bg-gray-100 ml-20 border border-gray-300 text-gray-800 font-semibold px-4 rounded shadow h-[30px]">
                일괄 정상출근
            </button>
        </SearchSectionForm>
    );
};
